import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "../../../shared/utils/logger";

function readStoredTimeoutMs(): number | null {
  const raw = localStorage.getItem("sessionTimeoutMs");
  if (!raw) return null;
  const ms = Number(raw);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

async function loadTimeoutFromEnv(): Promise<number> {
  try {
    const res = await fetch("/env", { credentials: "same-origin" });
    const env = await res.json();

    const minutesRaw = env.sessionTimeout;
    const minutes = Number(minutesRaw);
    const timeoutMs =
        Number.isFinite(minutes) && minutes > 0 ? minutes * 60 * 1000 : 10 * 60 * 1000;

    localStorage.setItem("sessionTimeoutMs", String(timeoutMs));
    logger.debug(
        `[/env] sessionTimeout (min): ${minutesRaw} → stored sessionTimeoutMs: ${timeoutMs}`
    );
    return timeoutMs;
  } catch (e) {
    logger.warn("Failed to fetch /env; defaulting to 10 min", e);
    const fallback = 10 * 60 * 1000;
    localStorage.setItem("sessionTimeoutMs", String(fallback));
    return fallback;
  }
}

export const useSessionTimerCore = (initialDurationMs?: number) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerExpired, setTimerExpired] = useState(false);

  const initial = (initialDurationMs && initialDurationMs > 0
      ? initialDurationMs
      : readStoredTimeoutMs()) ?? 0;

  const durationRef = useRef<number>(initial);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const getRemainingTime = useCallback(() => {
    const stored = localStorage.getItem("requestStartTime");
    if (!stored) return 0;
    const startTime = parseInt(stored, 10);
    const elapsed = Date.now() - startTime;
    const remaining = durationRef.current - elapsed;
    return remaining > 0 ? remaining : 0;
  }, []);

  const updateTimer = useCallback(() => {
    const remaining = getRemainingTime();
    if (remaining <= 0) {
      logger.debug("[SessionTimer] Countdown reached 0 → timer expired");
      setTimeLeft(0);
      setTimerExpired(true);
      localStorage.removeItem("requestStartTime");
      clearTimer();
    } else {
      setTimeLeft(remaining);
      setTimerExpired(false);
    }
  }, [getRemainingTime, clearTimer]);

  const startTicking = useCallback(() => {
    clearTimer();
    updateTimer();
    intervalRef.current = setInterval(updateTimer, 1000);
  }, [clearTimer, updateTimer]);

  const startTimer = useCallback(async () => {
    logger.debug("[SessionTimer] startTimer() called");

    const ms = await loadTimeoutFromEnv();
    logger.debug("[SessionTimer] Loaded from /env:", ms, "ms");

    durationRef.current = ms;
    logger.debug("[SessionTimer] Duration set (ms):", durationRef.current);

    const now = Date.now();
    localStorage.setItem("requestStartTime", String(now));
    logger.debug("[SessionTimer] requestStartTime saved:", now);

    setTimerExpired(false);
    setTimeLeft(ms);
    logger.debug(`[SessionTimer] Timer started: ${(ms / 1000).toFixed(0)}s`);

    startTicking();
    logger.debug("[SessionTimer] 1s ticking started");
  }, [startTicking]);

  const resetSessionTimer = useCallback(() => {
    const ms = readStoredTimeoutMs();
    if (ms == null) {
      logger.warn("[SessionTimer] resetSessionTimer(): no stored timeout; call startTimer() instead.");
      return;
    }
    durationRef.current = ms;
    localStorage.setItem("requestStartTime", String(Date.now()));
    setTimerExpired(false);
    setTimeLeft(ms);
    startTicking();
  }, [startTicking]);

  const disableTimer = useCallback(() => {
    clearTimer();
    setTimeLeft(0);
    setTimerExpired(false);
    localStorage.removeItem("requestStartTime");
  }, [clearTimer]);

  const resetTimerExpired = useCallback(() => setTimerExpired(false), []);

  const disableAllSessionTimeouts = useCallback(() => {
    logger.debug("[SessionTimer] disableAllSessionTimeouts() → clearing timer + disabling expiry notice");
    clearTimer();
    setTimeLeft(0);
    setTimerExpired(false);
    localStorage.removeItem("requestStartTime");
    window.dispatchEvent(new Event("sessionTimerDisabled"));
  }, [clearTimer]);

  useEffect(() => {
    const hasStart = localStorage.getItem("requestStartTime");
    if (hasStart) {
      const stored = readStoredTimeoutMs();
      if (stored != null) {
        durationRef.current = stored;
        logger.debug("[SessionTimer] Resuming with stored sessionTimeoutMs:", stored);
      } else {
        logger.warn("[SessionTimer] requestStartTime exists but no sessionTimeoutMs; will fetch on next startTimer()");
      }
      startTicking();
    } else {
      setTimeLeft(0);
      setTimerExpired(false);
    }
    return clearTimer;
  }, [startTicking, clearTimer]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") updateTimer();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [updateTimer]);

  return {
    timeLeft,
    timerExpired,
    startTimer,
    resetSessionTimer,
    disableTimer,
    resetTimerExpired,
    getRemainingTime,
    disableAllSessionTimeouts,
  };
};

const useSessionTimer = (initialDurationMs?: number) => {
  return useSessionTimerCore(initialDurationMs);
};

export default useSessionTimer;
